import React, { useEffect, useState } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { ClockClockwise, Exam } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import AssignmentGradingSection from "./AssignmentGradingSection";
import AssignmentAttemptHistory from "./AssignmentAttemptHistory";
import { useGetAssignmentAndSubmissionBySubmissionIdQuery } from "../../../../graphql/generated/graphql";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";
import FileSubmissionItem from "./StudentSubmission/FileSubmissionItem";
import { formatDateWithTime, isSubmissionLate } from "../../../../utils/date";

const getStatusColor = (status: string) => {
  switch (status) {
    case "RUNNING":
      return { bg: "#fef3c7", text: "#92400e" };
    case "NEEDS_REVIEW":
    case "Needs Review":
      return { bg: "#fecaca", text: "#dc2626" };
    case "COMPLETED":
    case "Completed":
      return { bg: "#d1fae5", text: "#065f46" };
    case "RETAKE_ALLOWED":
      return { bg: "#e0f2fe", text: "#0369a1" };
    default:
      return { bg: "#f3f4f6", text: "#6b7280" };
  }
};

const AssignmentGradingPanel: React.FC = () => {
  const { selectedSubmissionData, setUpdatedAssignmentPayload } = useGradingCenterSubmissions();
  const [activeTab, setActiveTab] = useState("submission");

  const { data: assignmentSubmissionDetails } = useGetAssignmentAndSubmissionBySubmissionIdQuery({
    variables: { submissionId: selectedSubmissionData?.id || "" },
    skip: !selectedSubmissionData?.id || selectedSubmissionData?.submissionType === "Assessment",
  });

  const assignment = assignmentSubmissionDetails?.getAssignmentAndSubmissionBySubmissionId?.data[0]?.assignment;
  const submission = assignmentSubmissionDetails?.getAssignmentAndSubmissionBySubmissionId?.data[0]?.submission;

  useEffect(() => {
    const grade = submission?.grade ?? selectedSubmissionData?.grade ?? null;
    const feedback = submission?.feedback ?? "";

    setUpdatedAssignmentPayload({
      grade,
      feedback,
      retakeAllowed: assignment?.usersHasSchoolAssignmentConnection?.edges[0]?.properties?.retakeAllowed ?? false,
      retakeDeadline: assignment?.usersHasSchoolAssignmentConnection?.edges[0]?.properties?.retakeDeadline
        ? new Date(assignment.usersHasSchoolAssignmentConnection.edges[0].properties.retakeDeadline)
        : null,
    });
  }, [assignmentSubmissionDetails, submission, assignment, selectedSubmissionData]);

  const statusColors = getStatusColor(selectedSubmissionData?.status || "");

  const formattedDueDateLabel = (() => {
    const rawDueDate = selectedSubmissionData?.dueDate || assignment?.due_date;
    if (!rawDueDate) return null;
    const parsed = new Date(rawDueDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  })();

  const timeSpent = (() => {
    const startedAt = submission?.created;
    const completedAt = submission?.completedAt;
    if (!startedAt || !completedAt) return "0 min";

    const startedAtDate = new Date(startedAt);
    const completedAtDate = new Date(completedAt);
    const timeSpentMs = completedAtDate.getTime() - startedAtDate.getTime();
    const minutes = Math.floor(timeSpentMs / (1000 * 60));
    return Number.isNaN(minutes) ? "0 min" : `${minutes} min`;
  })();

  const isLate = isSubmissionLate({
    submittedDate: submission?.completedAt || submission?.created || selectedSubmissionData?.submittedOn,
    dueDate: selectedSubmissionData?.dueDate || assignment?.due_date,
    retakeDeadline:
      selectedSubmissionData?.retakeDeadline ||
      assignment?.usersHasSchoolAssignmentConnection?.edges[0]?.properties?.retakeDeadline,
  });

  return (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false} tabIndex={0}>
      {/* Assignment Tabs */}
      <View style={styles.assignmentTabs}>
        <CustomButton
          text="Submission & Grading"
          leftIcon={<Exam size={14} color={activeTab === "submission" ? "#3E68FE" : "#475467"} />}
          weight="normal"
          styleType={activeTab === "submission" ? "primaryTransparent" : "transparent"}
          onPress={() => setActiveTab("submission")}
          style={[styles.assignmentTab, activeTab === "submission" && styles.activeTab]}
          textStyle={[styles.assignmentTabText, activeTab === "submission" && styles.activeTabText]}
        />
        <CustomButton
          text="Attempt History"
          leftIcon={<ClockClockwise size={14} color={activeTab === "history" ? "#3E68FE" : "#475467"} />}
          weight="normal"
          styleType={activeTab === "history" ? "primaryTransparent" : "transparent"}
          onPress={() => setActiveTab("history")}
          style={[styles.assignmentTab, activeTab === "history" && styles.activeTab]}
          textStyle={[styles.assignmentTabText, activeTab === "history" && styles.activeTabText]}
        />
      </View>

      {/* Assignment Content */}
      <View style={styles.assignmentContent}>
        {/* Submission & Grading Tab */}
        {activeTab === "submission" && (
          <>
            {/* Assignment Info Header */}
            <View style={styles.assignmentInfoHeader}>
              <View style={styles.assignmentBasicInfo}>
                <CustomText
                  text={assignment?.name}
                  size="l"
                  weight="bold"
                  style={styles.assignmentTitle}
                  useTranslationText={false}
                />
                <View style={styles.assignmentMeta}>
                  {selectedSubmissionData?.courseName && (
                    <View style={styles.metaTag}>
                      <CustomText text={selectedSubmissionData.courseName} size="xs" useTranslationText={false} />
                    </View>
                  )}
                  {selectedSubmissionData?.moduleName && (
                    <View style={styles.metaTag}>
                      <CustomText text={selectedSubmissionData.moduleName} size="xs" useTranslationText={false} />
                    </View>
                  )}
                  {formattedDueDateLabel && (
                    <View style={styles.metaTag}>
                      <CustomText text={`Due: ${formattedDueDateLabel}`} size="xs" useTranslationText={false} />
                    </View>
                  )}
                </View>
              </View>
              <View style={styles.assignmentStatusInfo}>
                <View style={styles.submissionStatus}>
                  <View style={[styles.badge, { backgroundColor: statusColors.bg }]}>
                    <CustomText
                      text={selectedSubmissionData?.status}
                      size="xs"
                      style={[styles.statusText, { color: statusColors.text }]}
                    />
                  </View>
                  {/* {selectedSubmissionData?.isResubmission && (
              <View style={styles.resubmissionTag}>
                <ArrowClockwise size={14} color="#D97706" />
                <CustomText text="Resubmission" size="xs" style={styles.resubmissionText} />
              </View>
            )} */}
                </View>
              </View>
            </View>

            {/* Student Info */}
            <View style={styles.studentSubmissionInfo}>
              <View style={styles.studentDetails}>
                <View style={styles.studentAvatar}>
                  <CustomText
                    text={selectedSubmissionData?.student?.initials}
                    size="s"
                    weight="bold"
                    style={styles.avatarText}
                    useTranslationText={false}
                  />
                </View>
                <View style={styles.studentInfo}>
                  <CustomText
                    text={selectedSubmissionData?.student?.name}
                    size="s"
                    weight="bold"
                    useTranslationText={false}
                  />
                  <CustomText
                    text={selectedSubmissionData?.student?.email}
                    size="xs"
                    style={styles.email}
                    useTranslationText={false}
                  />
                </View>
              </View>
              <View style={styles.submissionDetails}>
                <View style={styles.submissionDetail}>
                  <CustomText text="submitted" size="xs" style={styles.submissionDetailLabel} />
                  <CustomText
                    text={formatDateWithTime(submission?.completedAt || selectedSubmissionData?.submittedOn || "")}
                    size="xs"
                    weight="bold"
                    useTranslationText={false}
                  />
                </View>
                {(selectedSubmissionData?.dueDate || assignment?.due_date) && (
                  <View style={styles.timingTagRow}>
                    <View style={[styles.timingTag, isLate ? styles.timingTagLate : styles.timingTagOnTime]}>
                      <CustomText
                        text={isLate ? "Late submission" : "On time"}
                        size="xs"
                        style={[styles.timingTagText, isLate ? styles.timingTagTextLate : styles.timingTagTextOnTime]}
                        useTranslationText={false}
                      />
                    </View>
                  </View>
                )}
              </View>
            </View>

            {/* Student Submission */}
            <View style={styles.studentSubmissionSection}>
              <CustomText text="Student Submission" weight="bold" style={styles.sectionTitle} />
              <View style={styles.filesContainer}>
                {submission?.files?.map((file) => <FileSubmissionItem key={file.id} file={file} />)}
              </View>
            </View>

            {/* Grading Section */}
            <AssignmentGradingSection />
          </>
        )}

        {/* Attempt History Tab */}
        {activeTab === "history" && (
          <View style={styles.assignmentTabContent}>
            <AssignmentAttemptHistory />
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  assignmentTabs: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  assignmentTab: {
    height: 50,
    paddingHorizontal: 24,
    paddingVertical: 16,
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FCFCFD",
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
    borderRadius: 0,
  },
  assignmentTabText: {
    fontSize: 14,
    color: "#475467",
  },
  activeTab: {
    borderBottomColor: "#3E68FE",
    backgroundColor: "#FCFCFD",
    color: "#2952FE",
  },
  activeTabText: {
    color: "#2952FE",
  },
  assignmentContent: {
    flex: 1,
    padding: 24,
  },
  assignmentTabContent: {
    display: "flex",
  },
  assignmentInfoHeader: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 20,
  },
  assignmentBasicInfo: {
    flex: 1,
  },
  assignmentTitle: {
    color: "#111827",
    marginBottom: 8,
  },
  assignmentMeta: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  metaTag: {
    backgroundColor: "#F2F4F7",
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  assignmentStatusInfo: {
    alignItems: "flex-end",
  },
  submissionStatus: {
    alignItems: "flex-end",
    gap: 8,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    alignSelf: "flex-start",
  },
  statusText: {
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  // resubmissionTag: {
  //   backgroundColor: "#FEF3C7",
  //   borderWidth: 1,
  //   borderColor: "#F59E0B",
  //   borderRadius: 20,
  //   paddingHorizontal: 10,
  //   paddingVertical: 6,
  //   flexDirection: "row",
  //   alignItems: "center",
  //   gap: 6,
  // },
  // resubmissionText: {
  //   color: "#D97706",
  // },
  studentSubmissionInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: "#BFDBFE",
    borderRadius: 10,
    padding: 16,
    marginBottom: 24,
  },
  studentDetails: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  studentAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "#3B82F6",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#FFFFFF",
  },
  studentInfo: {
    flex: 1,
  },
  email: {
    color: "#6B7280",
    marginTop: 2,
  },
  submissionDetails: {
    alignItems: "flex-end",
  },
  submissionDetail: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
  },
  submissionDetailLabel: {
    color: "#475467",
  },
  timingTagRow: {
    marginTop: 6,
    alignItems: "flex-end",
  },
  timingTag: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: "flex-start",
    borderWidth: 1,
  },
  timingTagLate: {
    backgroundColor: "#FEF2F2",
    borderColor: "#FCA5A5",
  },
  timingTagOnTime: {
    backgroundColor: "#ECFDF3",
    borderColor: "#A7F3D0",
  },
  timingTagText: {
    fontWeight: "600",
    fontSize: 10,
  },
  timingTagTextLate: {
    color: "#DC2626",
  },
  timingTagTextOnTime: {
    color: "#047857",
  },
  studentSubmissionSection: {
    marginBottom: 32,
  },
  sectionTitle: {
    color: "#374151",
    marginBottom: 8,
  },
  filesContainer: {
    gap: 12,
  },
});

export default AssignmentGradingPanel;
